import { useEffect, useMemo, useState, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, SelectionChangedEvent } from 'ag-grid-community'
import { AG_GRID_LOCALE_RU } from '../../../shared/ag-grid/locale'
import { matchesQueueRow } from '../queueTypes'
import type { QueueRow } from '../queueTypes'

/**
 * Строка «без предложения»: у позиции НЕТ НИ ОДНОЙ квоты с реальной ценой
 * (> 0) — менеджеру нечего дать в КП. При этом либо поставщики ответили
 * «позиции нет» (цена 0), либо запрос остался без ответа (state «Не найдено» /
 * «Поставщик не ответил»). Кандидаты на переотправку другому поставщику.
 *
 * Важно (уточнение Сергея 08.07): если среди квот есть хоть одна реальная
 * цена (например 3 ¥ + одна нулевая) — позиция НЕ «без предложения». Как
 * только по позиции заливается реальная цена, она уходит из фильтра.
 */
export function isNoOffer(r: Pick<QueueRow, '_raw'>): boolean {
  const realPriced = r._raw.quotesPriced - r._raw.quotesZero // квоты с ценой > 0
  if (realPriced > 0) return false
  return (
    r._raw.quotesZero > 0 ||
    r._raw.procurementState === 'NOT_FOUND' ||
    r._raw.procurementState === 'NO_RESPONSE'
  )
}

/**
 * Сворачиваемая секция очереди квот («В работе» / «Обработано») под основной
 * таблицей. Обобщение QueueProcessedSection (07.07.2026): добавлены чекбоксы
 * выбора строк с кнопкой «Сформировать запрос» (переотправка позиций другому
 * поставщику) и фильтр «Без предложения (0)» — квоты с ценой 0 / не ответил.
 */
export function QueueCollapsibleSection({
  title,
  hint,
  rows,
  colDefs,
  defaultColDef,
  expanded,
  onToggle,
  hasSearch,
  searchText,
  gridRef,
  onCellMouseDown,
  onCellMouseOver,
  selectable = false,
  onSendRequest,
  showZeroFilter = false,
}: {
  title: string
  /** Подпись справа в шапке секции. */
  hint?: string
  rows: QueueRow[]
  colDefs: ColDef<QueueRow>[]
  defaultColDef: ColDef
  expanded: boolean
  onToggle: () => void
  hasSearch: boolean
  searchText: string
  gridRef: React.RefObject<AgGridReact<QueueRow>>
  onCellMouseDown: (e: import('ag-grid-community').CellMouseDownEvent<QueueRow>) => void
  onCellMouseOver: (e: import('ag-grid-community').CellMouseOverEvent<QueueRow>) => void
  /** Чекбоксы выбора строк + панель «Сформировать запрос». */
  selectable?: boolean
  /** Открыть выбор поставщика по выбранным строкам секции. */
  onSendRequest?: (rows: QueueRow[]) => void
  /** Кнопка-фильтр «Без предложения (0)». */
  showZeroFilter?: boolean
}) {
  const [selected, setSelected] = useState<QueueRow[]>([])
  const [zeroOnly, setZeroOnly] = useState(false)

  // Сворачивание размонтирует грид (его выделение теряется), а свежий грид при
  // раскрытии selectionChanged не эмитит — без сброса панель «Выбрано: N»
  // показывала бы фантомный выбор при пустых чекбоксах.
  useEffect(() => {
    if (!expanded) setSelected([])
  }, [expanded])

  const zeroCount = useMemo(
    () => (showZeroFilter ? rows.filter(isNoOffer).length : 0),
    [showZeroFilter, rows],
  )
  const visibleRows = useMemo(
    () => (showZeroFilter && zeroOnly ? rows.filter(isNoOffer) : rows),
    [showZeroFilter, zeroOnly, rows],
  )

  const onSelectionChanged = useCallback((e: SelectionChangedEvent<QueueRow>) => {
    setSelected(e.api.getSelectedRows())
  }, [])

  // Поиск (quickFilter) скрывает строки, но выделение с них AG Grid не снимает —
  // в отправку и счётчик берём только видимые под текущим поиском строки.
  const effectiveSelected = useMemo(
    () => (hasSearch ? selected.filter((r) => matchesQueueRow(r, searchText)) : selected),
    [hasSearch, searchText, selected],
  )

  if (rows.length === 0) return null
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm mt-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {title}
          <span className="text-gray-400 font-normal">({rows.length})</span>
        </span>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </button>
      {expanded && (
        <>
          {(showZeroFilter || (selectable && effectiveSelected.length > 0)) && (
            <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-t border-gray-100">
              {showZeroFilter && (
                <button
                  type="button"
                  onClick={() => setZeroOnly((v) => !v)}
                  className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                    zeroOnly
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                  title="Показать только позиции с квотой «цена 0» (нет предложения) или state «Не найдено»/«Поставщик не ответил» — их можно выбрать и отправить другому поставщику"
                >
                  Без предложения (0) · {zeroCount}
                </button>
              )}
              {selectable && effectiveSelected.length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-sm text-gray-600">Выбрано: {effectiveSelected.length}</span>
                  <button
                    type="button"
                    onClick={() => onSendRequest?.(effectiveSelected)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0 0l-4-4m4 4l4-4"
                      />
                    </svg>
                    Сформировать запрос
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      gridRef.current?.api?.deselectAll()
                      // deselectAll на пустом выборе событие не эмитит — чистим явно.
                      setSelected([])
                    }}
                    className="text-xs text-gray-400 hover:text-gray-700"
                  >
                    × Снять выделение
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Симметрично активной: при поиске `domLayout="autoHeight"` — грид
             сам считает высоту по контенту (учитывает wrap длинных заметок).
             min-h-[200px] чтобы overlay «No Matching Rows» был читаем. */}
          <div
            className={`crm-table ag-theme-alpine border-t border-gray-200 ${
              hasSearch ? 'min-h-[200px]' : 'h-[min(480px,60vh)] min-h-[240px]'
            }`}
          >
            <AgGridReact<QueueRow>
              ref={gridRef}
              getRowId={({ data }) => data.id}
              rowData={visibleRows}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              localeText={AG_GRID_LOCALE_RU}
              quickFilterText={searchText}
              domLayout={hasSearch ? 'autoHeight' : 'normal'}
              rowSelection={
                selectable
                  ? {
                      mode: 'multiRow',
                      checkboxes: true,
                      headerCheckbox: true,
                      selectAll: 'filtered',
                      enableClickSelection: false,
                    }
                  : undefined
              }
              onSelectionChanged={selectable ? onSelectionChanged : undefined}
              onCellMouseDown={onCellMouseDown}
              onCellMouseOver={onCellMouseOver}
              rowHeight={44}
              headerHeight={40}
              suppressDragLeaveHidesColumns
              suppressCellFocus
            />
          </div>
        </>
      )}
    </div>
  )
}
